import { AnalyticsService } from '@core/services';
import { AngularLayout, FlexLayout } from '@rhtml/modifiers';
import { Inject } from '@rxdi/core';
import { Component, css, html, LitElement } from '@rxdi/lit-html';
import { DividerComponent } from '@rxdi/ui-kit/divider';
import { GoogleMapComponent } from '@shared/components/google-map/google-map.component';
import { ReactivumTheme } from '@shared/styles/theme';

/**
 * @customElement contact-component
 */
@Component({
  selector: 'contact-component',
  components: [DividerComponent, GoogleMapComponent],
  styles: [
    css`
      ${ReactivumTheme}

      :host {
        display: block;
        color: var(--text-primary);
      }

      .section-header {
        text-align: center;
        margin-bottom: 64px;
      }

      .section-description {
        color: var(--text-secondary);
        font-size: 1.125rem;
        line-height: 1.7;
        max-width: 600px;
        margin: 0 auto;
        font-weight: 400;
      }

      .contact-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 48px;
        max-width: 1200px;
        margin: 0 auto;
      }

      .contact-info {
        padding: 48px;
      }

      .contact-details {
        margin-top: 32px;
      }

      .contact-item {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 24px;
        color: var(--text-secondary);
        font-weight: 400;
      }

      .contact-item svg {
        width: 24px;
        height: 24px;
        color: var(--primary-color);
      }

      .contact-item a {
        color: var(--primary-color);
        text-decoration: none;
        transition: color 0.3s ease;
        font-weight: 500;
      }

      .contact-item a:hover {
        color: var(--primary-dark);
        text-decoration: underline;
      }

      .map-container {
        height: 100%;
        min-height: 400px;
        border-radius: var(--radius-xl);
        overflow: hidden;
        border: 1px solid var(--border-light);
        box-shadow: var(--shadow-sm);
      }

      @media (max-width: 1024px) {
        .contact-grid {
          grid-template-columns: 1fr;
          gap: 32px;
        }
      }

      @media (max-width: 768px) {
        .contact-info {
          padding: 32px;
        }

        .map-container {
          min-height: 300px;
        }
      }
    `,
  ],
  modifiers: [...FlexLayout, ...AngularLayout],
  template(this: ContactComponent) {
    return html`
      <section class="section" id="contact">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">Get In Touch</h2>
            <rx-divider></rx-divider>
            <p class="section-description">
              Ready to start your next project? Let's discuss how we can help you build exceptional web applications
              with our modern frameworks and tools.
            </p>
          </div>

          <div class="contact-grid">
            <div class="card contact-info">
              <h3 class="card-title">Reactive Solutions</h3>
              <p style="color: var(--text-secondary); line-height: 1.6; margin-bottom: 24px;">
                We specialize in modern web development using cutting-edge technologies and frameworks to deliver
                exceptional user experiences.
              </p>

              <div class="contact-details">
                <div class="contact-item">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                    />
                  </svg>
                  <a
                    @click=${() => this.analytics.track('contact_click', { type: 'email', action: 'click' })}
                    href="mailto:support@reactivum.com"
                    >support@reactivum.com</a
                  >
                </div>

                <div class="contact-item">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                    />
                  </svg>
                  <a
                    @click=${() => this.analytics.track('contact_click', { type: 'phone', action: 'click' })}
                    href="tel:+359876667537"
                    >+359 876-667-537</a
                  >
                </div>

                <div class="contact-item">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    />
                  </svg>
                  <span>Sofia, Bulgaria</span>
                </div>

                <div class="contact-item">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                    />
                  </svg>
                  <span>Available for new projects</span>
                </div>
              </div>
            </div>

            <div class="map-container">
              <google-map-component address="София, България, Леонардо Да Винчи 9"></google-map-component>
            </div>
          </div>
        </div>
      </section>
    `;
  },
})
export class ContactComponent extends LitElement {
  @Inject(AnalyticsService)
  private analytics: AnalyticsService;
}
